  // src/contexts/NotesContext.js
  import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
  import { getCurrentUser } from '../utils/authService';
  import * as notesApi from '../../api/notesApi';
  import { useCategories } from './CategoriesContext';
  import { debounce } from 'lodash';
  import DOMPurify from 'dompurify';

  const NotesContext = createContext();

  // Content sanitization function
  const sanitizeContent = (htmlContent) => {
    if (!htmlContent) return '';

    // First, detect the content type
    const isCodeNote = htmlContent.includes('class="ql-syntax"');
    const isPlainText = !htmlContent.includes('<');
    const hasFormatting = !isCodeNote && !isPlainText;

    // Handle each type appropriately
    if (isCodeNote) {
      // For code notes, strictly preserve code formatting only
      return DOMPurify.sanitize(htmlContent, {
        ALLOWED_TAGS: ['pre', 'code'],
        ALLOWED_ATTR: ['class', 'spellcheck'],
        ALLOWED_CLASSES: ['ql-syntax']
      });
    }

    if (isPlainText) {
      // For plain text, wrap in paragraph
      return `<p>${htmlContent}</p>`;
    }

    // For rich text content, preserve HTML formatting
    return DOMPurify.sanitize(htmlContent, {
      ALLOWED_TAGS: [
        'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 
        'span', 'strong', 'em', 'u', 
        'ul', 'ol', 'li', 
        'img', 'br',
        'a',
        'div'
      ],
      ALLOWED_ATTR: [
        'style', 'class', 
        'src',
        'alt',
        'href',
        'width',
        'height'
      ],
      ALLOWED_STYLES: [
        'color', 'background-color', 
        'font-size', 'font-weight', 
        'text-align', 'margin', 'padding'
      ]
    });
  };

  export function NotesProvider({ children }) {
    const { categories } = useCategories();
    const [notes, setNotes] = useState([]);
    const [selectedNoteId, setSelectedNoteId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [noteImages, setNoteImages] = useState({});
    const [subnotes, setSubnotes] = useState({});

    // Load initial notes
    useEffect(() => {
      loadNotes();
    }, []);

    useEffect(() => {
      console.log("🟢 Selected Note Changed:", selectedNoteId);
    }, [selectedNoteId, notes]);

    // Load subnotes when a note is selected
    useEffect(() => {
      const loadSubnotes = async () => {
        if (!selectedNoteId) return;
        
        try {
          // Only fetch subnotes for top-level notes (notes without a parent)
          const selectedNote = notes.find(note => note.id === selectedNoteId);
          if (selectedNote && !selectedNote.parent_id) {
            console.log(`Loading subnotes for parent note ${selectedNoteId}`);
            const subnotesData = await notesApi.fetchSubnotes(selectedNoteId);
            
            if (subnotesData.length > 0) {
              console.log(`Found ${subnotesData.length} subnotes for parent ${selectedNoteId}:`, 
                subnotesData.map(n => `${n.id}: ${n.title}`));
            }
            
            // Update subnotes state while preserving existing subnotes for other parents
            setSubnotes(prev => {
              const newSubnotes = {...prev};
              newSubnotes[selectedNoteId] = subnotesData;
              return newSubnotes;
            });
          }
        } catch (error) {
          console.error('Error loading subnotes:', error);
        }
      };
      
      loadSubnotes();
    }, [selectedNoteId, notes]);
    
// Debounced save handler
// Debounced save handler

const debouncedUpdateNote = useCallback(
  debounce(async (noteId, noteData) => {
    console.log("⏳ Debounce Timer Triggered...");
    if (!noteId || !noteData) return;
    
    try {
      // Don't re-sanitize if content is already properly formatted
      const isAlreadySanitized = 
        !noteData.note || 
        noteData.note.includes('<pre class="ql-syntax"') ||
        (noteData.note.startsWith('<') && !noteData.note.includes('ql-syntax'));
      
      const noteToUpdate = {
        ...noteData,
        note: isAlreadySanitized ? noteData.note : sanitizeContent(noteData.note),
        parent_id: noteData.parent_id // Ensure parent_id is preserved
      };
      
      // Use the API to update the note
      const updatedNote = await notesApi.updateNote(noteId, noteToUpdate);
      console.log(`✅ Saved Note ${noteId}`);
      
      // Update both main notes and subnotes state
      updateLocalState(noteId, updatedNote);
      
      return updatedNote;
    } catch (error) {
      console.error('Error in auto-save:', error);
      throw error; // Preserve original error propagation
    }
  }, 2000), // Restore original 3000ms timing
  []
);

// !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!! LAST WORKIN!!!
// const debouncedUpdateNote = useCallback(
//   debounce(async (noteId, noteData) => {
//     console.log(`⏳ Saving note ${noteId} to server...`);
    
//     try {
//       // Always ensure parent_id is included
//       const updatedNote = await notesApi.updateNote(noteId, noteData);
//       console.log(`✅ Saved note ${noteId}`);
      
//       // Update both main notes and subnotes state
//       updateLocalState(noteId, updatedNote);
      
//       return updatedNote;
//     } catch (error) {
//       console.error(`Failed to save note ${noteId}:`, error);
//     }
//   }, 2000),
//   []
// );

// Helper function to update local state
const updateLocalState = useCallback((noteId, updatedData) => {
  // Update main notes array
  setNotes(prev => prev.map(note => 
    note.id === noteId ? { ...note, ...updatedData } : note
  ));
  
  // Update subnotes if needed
  setSubnotes(prev => {
    let updated = false;
    const newSubnotes = { ...prev };
    
    for (const parentId in newSubnotes) {
      const index = newSubnotes[parentId].findIndex(n => n.id === noteId);
      if (index !== -1) {
        newSubnotes[parentId][index] = { ...newSubnotes[parentId][index], ...updatedData };
        updated = true;
        break;
      }
    }
    
    return updated ? newSubnotes : prev;
  });
}, []);

// Debounced save handler

// this is the working with subnotes but appears to break other stuff like tags keywords
// const debouncedUpdateNote = useCallback(
//   debounce(async (noteId, noteData) => {
//     console.log(`Debounced update for note ${noteId}`);
    
//     try {
//       // Use the API to update the note (noteData should already contain parent_id)
//       const updatedNote = await notesApi.updateNote(noteId, noteData);
//       console.log(`✅ Saved note ${noteId} successfully`);
      
//       // We don't need to update state here as it was already updated in updateNoteContent
//       return updatedNote;
//     } catch (error) {
//       console.error(`Failed to save note ${noteId}:`, error);
//     }
//   }, 2000),
//   []
// );

/////////////////////////////////////////////////////
/// SUPPOSED ORIGINAL INCOPRATED 
// Debounced save handler
// const debouncedUpdateNote = useCallback(
//   debounce(async (noteId, noteData) => {
//     console.log("⏳ Debounce Timer Triggered...");
//     if (!noteId || !noteData) return;
    
//     try {
//       // Don't re-sanitize if content is already properly formatted
//       const isAlreadySanitized = 
//         !noteData.note || 
//         noteData.note.includes('<pre class="ql-syntax"') ||
//         (noteData.note.startsWith('<') && !noteData.note.includes('ql-syntax'));
      
//       // Ensure parent_id is explicitly preserved in the update
//       const noteToUpdate = {
//         ...noteData,
//         note: isAlreadySanitized ? noteData.note : sanitizeContent(noteData.note),
//         parent_id: noteData.parent_id // Explicitly preserve parent_id
//       };
      
//       console.log(`Updating note ${noteId}, with parent_id=${noteData.parent_id}`);
      
//       // Use the API to update the note
//       const updatedNote = await notesApi.updateNote(noteId, noteToUpdate);
//       console.log(`✅ Saved Note ${noteId}`);
      
//       // Update local state with the response from the server
//       setNotes(prev => prev.map(note => 
//         note.id === noteId ? { ...note, ...updatedNote } : note
//       ));
      
//       // Also update subnotes state if needed
//       setSubnotes(prev => {
//         const newSubnotes = {...prev};
//         let updated = false;
        
//         for (const parentId in newSubnotes) {
//           const index = newSubnotes[parentId].findIndex(n => n.id === noteId);
//           if (index !== -1) {
//             newSubnotes[parentId] = newSubnotes[parentId].map(n => 
//               n.id === noteId ? { ...n, ...updatedNote } : n
//             );
//             updated = true;
//             break;
//           }
//         }
        
//         return updated ? newSubnotes : prev;
//       });
      
//       return updatedNote;
//     } catch (error) {
//       console.error('Error in auto-save:', error);
//       throw error;
//     }
//   }, 3000),
//   [subnotes] // Add subnotes to dependencies since we're using it
// ); 
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//// ORGINAL BEFORE SUBS NOTES WORK
  // Debounced save handler
  // const debouncedUpdateNote = useCallback(
  //   debounce(async (noteId, noteData) => {
  //     console.log("⏳ Debounce Timer Triggered...");
  //     if (!noteId || !noteData) return;
      
  //     try {
  //       // Don't re-sanitize if content is already properly formatted
  //       const isAlreadySanitized = 
  //         !noteData.note || 
  //         noteData.note.includes('<pre class="ql-syntax"') ||
  //         (noteData.note.startsWith('<') && !noteData.note.includes('ql-syntax'));
        
  //       const noteToUpdate = {
  //         ...noteData,
  //         note: isAlreadySanitized ? noteData.note : sanitizeContent(noteData.note)
  //       };
        
  //       // Use the API to update the note
  //       const updatedNote = await notesApi.updateNote(noteId, noteToUpdate);
  //       console.log(`✅ Saved Note ${noteId}`);
        
  //       // Update local state with the response from the server
  //       setNotes(prev => prev.map(note => 
  //         note.id === noteId ? { ...note, ...updatedNote } : note
  //       ));
        
  //       return updatedNote;
  //     } catch (error) {
  //       console.error('Error in auto-save:', error);
  //       throw error;
  //     }
  //   }, 3000),
  //   []
  // );
  ////////////////////////////////////////////////////////////////////////////

  
  

    // Fetch all notes using API
  // In NotesContext.js
  const loadNotes = async () => {
    try {
      setLoading(true);
      const fetchedNotes = await notesApi.fetchNotes();
      
      // console.log("All notes from API:", fetchedNotes.map(n => ({
      //   id: n.id, 
      //   title: n.title, 
      //   parent_id: n.parent_id
      // })));
      
      // Split notes into parent notes and subnotes
      const topLevelNotes = fetchedNotes.filter(note => !note.parent_id);
      const childNotes = fetchedNotes.filter(note => note.parent_id);
      
      /// console.log(`Found ${topLevelNotes.length} top-level notes and ${childNotes.length} subnotes`);
      
      // Organize subnotes by parent_id
      const subnotesByParent = {};
      childNotes.forEach(note => {
        const parentId = note.parent_id;
        if (!subnotesByParent[parentId]) {
          subnotesByParent[parentId] = [];
        }
        subnotesByParent[parentId].push(note);
      });
      
      // Update state
      setNotes(topLevelNotes);
      setSubnotes(subnotesByParent);
      
      // Select first note if none selected
      if (!selectedNoteId && topLevelNotes.length > 0) {
        setSelectedNoteId(topLevelNotes[0].id);
      }
      
      setError(null);
    } catch (err) {
      setError('Failed to load notes');
      console.error('Error loading notes:', err);
    } finally {
      setLoading(false);
    }
  };

    // Create a new note using API
    const createNote = useCallback(async () => {
      try {
        setLoading(true);
        const user = getCurrentUser();
        
        const defaultCategory = categories.find(cat => cat.is_special === true);
        if (!defaultCategory) {
          throw new Error('No default category found');
        }
        
        // Use the API to create a note
        const createdNote = await notesApi.createNote(user.user_id, defaultCategory.id);
        
        // Add the new note to the notes array rather than fetching all notes again
        setNotes(prev => [...prev, createdNote]);
        
        // Select the new note
        setSelectedNoteId(createdNote.id);
        
        return createdNote;
      } catch (err) {
        setError('Failed to create note');
        throw err;
      } finally {
        setLoading(false);
      }
    }, [categories]);

    // Update note content
  // In NotesContext.js
  const updateNoteContent = useCallback((noteId, content) => {
    if (!noteId) return;
    
    // Check if the note is in the main notes array
    let note = notes.find(n => n.id === noteId);
    let isMainNote = true;
    
    // If not in main notes, look for it in subnotes
    if (!note) {
      isMainNote = false;
      for (const parentId in subnotes) {
        const found = subnotes[parentId]?.find(n => n.id === noteId);
        if (found) {
          note = found;
          break;
        }
      }
    }
    
    if (!note) {
      console.error(`Note ${noteId} not found in any state`);
      return;
    }
    
    // Skip if content hasn't changed
    if (note.note === content) {
      console.log("No change detected, skipping update");
      return;
    }
    
    console.log(`Updating content for note ${noteId}`);
    
    // Create updated note object
    const updatedNote = { ...note, note: content };
    
    // Update the appropriate state immediately for UI responsiveness
    if (isMainNote) {
      setNotes(prev => prev.map(n => n.id === noteId ? updatedNote : n));
    } else {
      setSubnotes(prev => {
        const newSubnotes = { ...prev };
        for (const parentId in newSubnotes) {
          const index = newSubnotes[parentId].findIndex(n => n.id === noteId);
          if (index !== -1) {
            newSubnotes[parentId] = [
              ...newSubnotes[parentId].slice(0, index),
              updatedNote,
              ...newSubnotes[parentId].slice(index + 1)
            ];
            break;
          }
        }
        return newSubnotes;
      });
    }
    
    // Send update to server with debouncing
    debouncedUpdateNote(noteId, updatedNote);
  }, [notes, subnotes, debouncedUpdateNote]);

    // Update note title
// Update note title
const updateNoteTitle = useCallback((noteId, title) => {
  if (!noteId) return;
  
  // Find the note in either main notes or subnotes
  let note = notes.find(n => n.id === noteId);
  let isMainNote = true;
  
  if (!note) {
    isMainNote = false;
    for (const parentId in subnotes) {
      const found = subnotes[parentId]?.find(n => n.id === noteId);
      if (found) {
        note = found;
        break;
      }
    }
  }
  
  if (!note) {
    console.error(`Note ${noteId} not found for title update`);
    return;
  }
  
  // Skip if title hasn't changed
  if (note.title === title) return;
  
  console.log(`Updating title for note ${noteId} to: ${title}`);
  
  // Create updated note object, preserving all fields including parent_id
  const updatedNote = { ...note, title };
  
  // Update state immediately for UI responsiveness
  if (isMainNote) {
    setNotes(prev => prev.map(n => n.id === noteId ? updatedNote : n));
  } else {
    setSubnotes(prev => {
      const newSubnotes = { ...prev };
      for (const parentId in newSubnotes) {
        const index = newSubnotes[parentId].findIndex(n => n.id === noteId);
        if (index !== -1) {
          newSubnotes[parentId] = [
            ...newSubnotes[parentId].slice(0, index),
            updatedNote,
            ...newSubnotes[parentId].slice(index + 1)
          ];
          break;
        }
      }
      return newSubnotes;
    });
  }
  
  // Send update to server with debouncing
  debouncedUpdateNote(noteId, updatedNote);
}, [notes, subnotes, debouncedUpdateNote]);

const updateNoteTags = useCallback((noteId, tags) => {
  if (!noteId) return;
  
  // First check if it's in main notes
  let note = notes.find(n => n.id === noteId);
  let isMainNote = true;
  
  // If not found in main notes, check subnotes
  if (!note) {
    isMainNote = false;
    for (const parentId in subnotes) {
      const foundNote = subnotes[parentId]?.find(n => n.id === noteId);
      if (foundNote) {
        note = foundNote;
        break;
      }
    }
  }
  
  if (!note) return;
  
  // Format tags correctly (ensure it's a string)
  let formattedTags = tags;
  if (Array.isArray(tags)) {
    formattedTags = JSON.stringify(tags);
  }
  
  // Create updated note object
  const updatedNote = { ...note, tags: formattedTags };
  
  // Update the appropriate state immediately for UI responsiveness
  if (isMainNote) {
    setNotes(prev => prev.map(n => n.id === noteId ? updatedNote : n));
  } else {
    setSubnotes(prev => {
      const newSubnotes = { ...prev };
      for (const parentId in newSubnotes) {
        const index = newSubnotes[parentId].findIndex(n => n.id === noteId);
        if (index !== -1) {
          newSubnotes[parentId] = [
            ...newSubnotes[parentId].slice(0, index),
            updatedNote,
            ...newSubnotes[parentId].slice(index + 1)
          ];
          break;
        }
      }
      return newSubnotes;
    });
  }
  
  // Use direct API call instead of debouncing for more immediate saving
  notesApi.updateNote(noteId, updatedNote)
    .then(() => console.log(`Tags saved for note ${noteId}`))
    .catch(error => console.error('Error saving tags:', error));
}, [notes, subnotes]);

    // Update note category
    const updateNoteCategory = useCallback((noteId, categoryId) => {
      if (!noteId) return;
      
      const note = notes.find(n => n.id === noteId);
      if (!note) return;
      
      const updatedNote = {
        ...note,
        primary_category_id: parseInt(categoryId)
      };
      
      // Update local state immediately for responsiveness
      setNotes(prev => prev.map(n => n.id === noteId ? updatedNote : n));
      
      // Send update to server with debouncing
      debouncedUpdateNote(noteId, updatedNote);
    }, [notes, debouncedUpdateNote]);

    // Refresh note images using API
    const refreshNoteImages = useCallback(async (noteId) => {
      if (!noteId) return [];
      
      try {
        console.log('NotesContext: refreshNoteImages called for note', noteId);
        
        // Use the API to fetch images
        const images = await notesApi.fetchNoteImages(noteId);
        console.log('New images fetched:', images);
        
        // Update the images in state
        setNoteImages(prev => ({
          ...prev,
          [noteId]: images
        }));
        
        return images;
      } catch (error) {
        console.error('Error refreshing images:', error);
        return [];
      }
    }, []);

    // Get note images from local state
    const getNoteImages = useCallback((noteId) => {
      return noteImages[noteId] || [];
    }, [noteImages]);

    // Delete a note using API
  // Delete a note using API
  const deleteNote = useCallback(async (noteId) => {
    try {
      // Use the API to delete the note
      await notesApi.deleteNote(noteId);
      
      // Check if it's a main note
      const mainNoteIndex = notes.findIndex(note => note.id === noteId);
      
      if (mainNoteIndex !== -1) {
        // It's a main note - remove it from notes
        setNotes(prev => prev.filter(note => note.id !== noteId));
        
        // Remove subnotes from state
        const updatedSubnotes = { ...subnotes };
        delete updatedSubnotes[noteId];
        setSubnotes(updatedSubnotes);
        
        // Select another note if available
        const remainingNotes = notes.filter(n => n.id !== noteId);
        if (remainingNotes.length > 0) {
          setSelectedNoteId(remainingNotes[0].id);
        } else {
          setSelectedNoteId(null);
        }
      } else {
        // Check if it's a subnote
        for (const parentId in subnotes) {
          const subnoteIndex = subnotes[parentId]?.findIndex(note => note.id === noteId);
          
          if (subnoteIndex !== -1) {
            // Update subnotes state
            setSubnotes(prev => ({
              ...prev,
              [parentId]: prev[parentId].filter(note => note.id !== noteId)
            }));
            
            // Select parent note
            setSelectedNoteId(parseInt(parentId));
            break;
          }
        }
      }
      
      return true;
    } catch (err) {
      setError('Failed to delete note');
      throw err;
    }
  }, [notes, subnotes]);

    // Select a note
    const selectNote = useCallback((noteId) => {
      console.log("🔵 selectNote() Called with:", noteId);
      setSelectedNoteId(noteId);
    }, []);

    // Create a new subnote
const createSubnote = useCallback(async (parentNoteId) => {
  try {
    // Check if the parent is already a subnote
    let isParentASubnote = false;
    
    // Search through all subnotes
    for (const parentId in subnotes) {
      if (subnotes[parentId].some(note => note.id === parentNoteId)) {
        isParentASubnote = true;
        break;
      }
    }
    
    if (isParentASubnote) {
      // Parent is already a subnote, don't allow creating a sub-subnote
      alert("Cannot create a subnote within another subnote. Only one level of nesting is allowed.");
      return null;
    }
    
    setLoading(true);
    const user = getCurrentUser();
    
    const defaultCategory = categories.find(cat => cat.is_special === true);
    const defaultCategoryId = defaultCategory?.id;
    
    console.log(`Creating subnote under parent ${parentNoteId}`);
    
    // Use the API to create a subnote
    const newSubnote = await notesApi.createSubnote(parentNoteId, user.user_id, defaultCategoryId);
    
    console.log(`Successfully created subnote ${newSubnote.id} for parent ${parentNoteId}`, newSubnote);
    
    // Update subnotes state
    setSubnotes(prev => {
      const newSubnotes = {...prev};
      if (!newSubnotes[parentNoteId]) {
        newSubnotes[parentNoteId] = [];
      }
      newSubnotes[parentNoteId] = [...newSubnotes[parentNoteId], newSubnote];
      return newSubnotes;
    });
    
    // Select the new subnote
    setSelectedNoteId(newSubnote.id);
    return newSubnote;
  } catch (err) {
    console.error('Error creating subnote:', err);
    setError('Failed to create subnote');
    throw err;
  } finally {
    setLoading(false);
  }
}, [categories, setSelectedNoteId, subnotes]);

    // Get the currently selected note
  // Get the currently selected note
  const getSelectedNote = useCallback(() => {
    if (!selectedNoteId) return null;
    
    // First check if it's a main note
    const mainNote = notes.find(note => note.id === selectedNoteId);
    if (mainNote) {
      ///console.log(`Found note ${selectedNoteId} in main notes`);
      return mainNote;
    }
    
    // Then check if it's a subnote
    for (const parentId in subnotes) {
      const foundSubnote = subnotes[parentId]?.find(note => note.id === selectedNoteId);
      if (foundSubnote) {
        /// console.log(`Found note ${selectedNoteId} as subnote of ${parentId}`);
        return foundSubnote;
      }
    }
    
   // console.log(`Note ${selectedNoteId} not found in either main notes or subnotes`);
    return null;
  }, [notes, selectedNoteId, subnotes]);

    // Clean up on unmount
    useEffect(() => {
      return () => {
        debouncedUpdateNote.cancel();
      };
    }, [debouncedUpdateNote]);

    const value = {
      notes,
      subnotes,   
      selectedNoteId,
      loading,
      error,
      createNote,
      createSubnote, 
      updateNote: debouncedUpdateNote,    // Renamed for consistency
      updateNoteContent,                  // New method
      updateNoteTitle,                    // New method
      updateNoteTags, 
      updateNoteCategory,                 // New method
      deleteNote,
      selectNote,
      getSelectedNote,
      refreshNotes: loadNotes,
      refreshNoteImages,                  // New method
      getNoteImages                       // New method
    };

    return (
      <NotesContext.Provider value={value}>
        {children}
      </NotesContext.Provider>
    );
  }

  export function useNotes() {
    const context = useContext(NotesContext);
    if (!context) {
      throw new Error('useNotes must be used within a NotesProvider');
    }
    return context;
  }